/**
 * CrewAccounting.jsx — one unified payroll view for everyone.
 *
 * Every person sees the SAME thing: their earnings grid + their payment history.
 * Admins additionally get a person switcher (pills with owed badges) to view/pay
 * anyone, and a "Close & pay" action. Crew are locked to themselves. No separate
 * roster abstraction, no nested expand.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { fmtC, fmtD, cn } from '../../lib/utils';
import { useRoster } from '../../hooks/useRoster';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC } from '../../hooks/useRBAC';
import { useCrewEarnings, useUpdateCrewEarning } from '../../hooks/useCrewEarnings';
import { usePayPeriodPayments, useCreatePayPeriodPayment, useUpdatePayPeriodPayment, useDeletePayPeriodPayment } from '../../hooks/usePayroll';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';
import CrewEarningsGrid from './CrewEarningsGrid';

const PAYMENT_METHODS = ['Venmo', 'Zelle', 'Check', 'Cash / Bank transfer', 'Gusto', 'Credit Card'];
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const firstName = (n) => (n || '').split(' ')[0];

export default function CrewAccounting() {
  const { hasAccess } = useRBAC();
  const { profile } = useAuth();
  const { data: roster = [] } = useRoster();
  const { data: earnings = [] } = useCrewEarnings();
  const isAdmin = hasAccess('payroll', 'full');
  const myName = profile?.full_name || '';

  const owedByPerson = useMemo(() => {
    const m = {};
    earnings.forEach((e) => {
      if (e.paidAt) return;
      const p = e.person;
      if (!p) return;
      m[p] = (m[p] || 0) + (parseFloat(e.amount) || 0);
    });
    return m;
  }, [earnings]);

  // People to show: self first, then core crew, then anyone with earnings.
  const people = useMemo(() => {
    if (!isAdmin) return myName ? [myName] : [];
    const set = new Set();
    if (myName) set.add(myName);
    roster.forEach((r) => { if (r.name && (r.isCore || r.is_core)) set.add(r.name); });
    Object.keys(owedByPerson).forEach((n) => { set.add(n); });
    return [...set];
  }, [isAdmin, myName, roster, owedByPerson]);

  const [selected, setSelected] = useState('');
  useEffect(() => { if (!selected && myName) setSelected(myName); }, [myName, selected]);
  const person = isAdmin ? (selected || myName) : myName;

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto">
      {isAdmin ? (
        <div className="flex items-center gap-1.5 flex-wrap">
          {people.map((name) => {
            const owed = owedByPerson[name] || 0;
            const active = name === person;
            return (
              <button
                key={name} onClick={() => setSelected(name)}
                className={cn(
                  'px-3 py-1.5 rounded-full border text-xs transition-colors flex items-center gap-1.5',
                  active ? 'bg-accent text-bg border-accent font-semibold' : 'bg-surface border-border text-text-primary hover:border-accent',
                )}
              >
                {name === myName ? 'You' : firstName(name)}
                {owed > 0 && <span className={cn('font-mono', active ? 'text-bg/90' : 'text-green')}>{fmtC(owed)}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-2xs text-text-dim uppercase tracking-wider">Your earnings</div>
      )}

      <PersonPanel key={person} person={person} isAdmin={isAdmin} owed={owedByPerson[person] || 0} earnings={earnings} />
    </div>
  );
}

function PersonPanel({ person, isAdmin, owed, earnings }) {
  const { toast } = useToast();
  const { data: payments = [] } = usePayPeriodPayments();
  const createPayment = useCreatePayPeriodPayment();
  const updatePayment = useUpdatePayPeriodPayment();
  const updateEarning = useUpdateCrewEarning();

  const today = useMemo(() => fmtISO(new Date()), []);
  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState(PAYMENT_METHODS[0]);
  const [payDate, setPayDate] = useState(today);
  const [payNote, setPayNote] = useState('');
  const [saving, setSaving] = useState(false);

  const lastPayEnd = useMemo(() => {
    let end = '';
    payments.forEach((p) => {
      if (!p.closed) return;
      if ((p.personName || p.person_name) !== person) return;
      const e = (p.periodKey || p.period_key || '').split('_')[1] || '';
      if (e > end) end = e;
    });
    return end;
  }, [payments, person]);

  const openPay = () => {
    setPayAmount((Math.round(owed * 100) / 100).toString());
    setPayMethod(PAYMENT_METHODS[0]);
    setPayDate(today);
    setPayNote('');
    setShowPay(true);
  };

  const submitPay = useCallback(async () => {
    const amount = parseFloat(payAmount);
    if (!(amount >= 0)) return toast('Enter the amount paid', 'error');
    if (!payDate) return toast('Pick the payment date', 'error');
    setSaving(true);
    try {
      const start = lastPayEnd || '2000-01-01';
      const entry = { amount, method: payMethod, date: payDate, note: payNote.trim() || null };
      const prow = await createPayment.mutateAsync({ periodKey: `${start}_${payDate}`, personName: person, payments: [], closed: false });
      await updatePayment.mutateAsync({ id: prow.id, payments: [entry], actual: amount, closed: true });
      const now = new Date().toISOString();
      const unpaid = earnings.filter((e) => e.person === person && !e.paidAt);
      for (const e of unpaid) await updateEarning.mutateAsync({ id: e.id, paidAt: now, paidViaPaymentId: prow.id });
      logAction({
        action: 'Pay Crew',
        description: `Paid ${person} ${fmtC(amount)} via ${payMethod} — ${unpaid.length} item${unpaid.length === 1 ? '' : 's'} settled`,
        details: { person, amount, method: payMethod, date: payDate, itemsSettled: unpaid.length, periodKey: `${start}_${payDate}` },
      });
      toast(`Paid ${person} ${fmtC(amount)} via ${payMethod} — ${unpaid.length} item${unpaid.length === 1 ? '' : 's'} settled`);
      setShowPay(false);
    } catch (err) {
      toast(err?.message ? `Failed: ${err.message}` : 'Failed to record payment', 'error');
    } finally {
      setSaving(false);
    }
  }, [payAmount, payMethod, payDate, payNote, lastPayEnd, person, earnings, createPayment, updatePayment, updateEarning, toast]);

  const fieldCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent';
  const lblCls = 'block text-xs font-semibold text-text-dim mb-1';

  return (
    <div className="flex flex-col gap-2">
      {/* Owed + pay */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          {isAdmin && <div className="text-sm font-semibold text-text-primary">{person}</div>}
          <div className="text-2xs text-text-dim uppercase tracking-wider">Owed now</div>
          <div className="text-2xl font-heading text-green leading-tight">{fmtC(owed)}</div>
          {lastPayEnd && <div className="text-2xs text-text-dim">last paid {fmtD(lastPayEnd)}</div>}
        </div>
        {isAdmin && owed > 0 && (
          <button onClick={openPay} className="px-3.5 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors">Close &amp; pay</button>
        )}
      </div>

      {/* Earnings grid */}
      <CrewEarningsGrid person={person} />

      {/* Payment history */}
      <PaymentHistory person={person} payments={payments} earnings={earnings} isAdmin={isAdmin} />

      {/* Close & pay modal */}
      {showPay && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="card p-5 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-heading text-text-primary">Pay {person}</h3>
              <button onClick={() => setShowPay(false)} className="text-text-dim hover:text-text-primary"><CloseRoundedIcon /></button>
            </div>
            <div className="text-2xs text-text-dim uppercase tracking-wider">Owed this period</div>
            <div className="text-2xl font-heading text-green mb-3">{fmtC(owed)}</div>
            <div className="flex flex-col gap-3">
              <div><label className={lblCls}>Amount paid</label><input type="number" min="0" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className={fieldCls} /></div>
              <div><label className={lblCls}>Method</label><select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={fieldCls}>{PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
              <div><label className={lblCls}>Payment date</label><input type="date" max={today} value={payDate} onChange={(e) => setPayDate(e.target.value)} className={fieldCls} /></div>
              <div><label className={lblCls}>Note <span className="font-normal text-text-dim">(optional)</span></label><input type="text" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="e.g. June wrap-up" className={fieldCls} /></div>
              <button onClick={submitPay} disabled={saving} className="mt-1 px-4 py-2.5 bg-accent text-bg text-sm font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50">
                {saving ? 'Recording…' : 'Record payment & close period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentHistory({ person, payments, earnings, isAdmin }) {
  const { toast } = useToast();
  const updatePayment = useUpdatePayPeriodPayment();
  const deletePayment = useDeletePayPeriodPayment();
  const updateEarning = useUpdateCrewEarning();

  const [openId, setOpenId] = useState(null);
  const [edit, setEdit] = useState(null); // { id, amount, method, date, note }
  const [busy, setBusy] = useState(false);

  const history = useMemo(() => {
    return payments
      .filter((p) => p.closed && (p.personName || p.person_name) === person)
      .map((p) => {
        const pay = (p.payments && p.payments[0]) || {};
        const items = earnings.filter((e) => e.person === person && e.paidViaPaymentId === p.id);
        const date = pay.date || (p.periodKey || p.period_key || '').split('_')[1] || '';
        return { id: p.id, date, method: pay.method || '—', amount: p.actual != null ? p.actual : pay.amount, note: pay.note || '', items };
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [payments, person, earnings]);

  const handleDelete = useCallback(async (h) => {
    if (!window.confirm(`Delete this ${fmtC(h.amount)} payment to ${person}?\nIts ${h.items.length} line item${h.items.length === 1 ? '' : 's'} will go back to "owed".`)) return;
    setBusy(true);
    try {
      for (const it of h.items) await updateEarning.mutateAsync({ id: it.id, paidAt: null, paidViaPaymentId: null });
      await deletePayment.mutateAsync(h.id);
      logAction({
        action: 'Delete Payment',
        description: `Deleted ${fmtC(h.amount)} payment to ${person} — ${h.items.length} item${h.items.length === 1 ? '' : 's'} returned to owed`,
        details: { person, amount: h.amount, method: h.method, itemsReturned: h.items.length },
      });
      toast(`Payment deleted — ${h.items.length} item${h.items.length === 1 ? '' : 's'} returned to owed`);
    } catch (e) {
      toast(e?.message ? `Delete failed: ${e.message}` : 'Delete failed', 'error');
    } finally { setBusy(false); }
  }, [person, updateEarning, deletePayment, toast]);

  const saveEdit = useCallback(async () => {
    const amount = parseFloat(edit.amount);
    if (!(amount >= 0)) return toast('Enter the amount', 'error');
    if (!edit.date) return toast('Pick the date', 'error');
    setBusy(true);
    try {
      await updatePayment.mutateAsync({ id: edit.id, payments: [{ amount, method: edit.method, date: edit.date, note: edit.note.trim() || null }], actual: amount, closed: true });
      logAction({
        action: 'Adjust Payment',
        description: `Adjusted ${person}'s payment to ${fmtC(amount)} via ${edit.method}`,
        details: { person, amount, method: edit.method, date: edit.date },
      });
      toast('Payment updated');
      setEdit(null);
    } catch (e) {
      toast(e?.message ? `Update failed: ${e.message}` : 'Update failed', 'error');
    } finally { setBusy(false); }
  }, [edit, person, updatePayment, toast]);

  const fieldCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent';
  const lblCls = 'block text-xs font-semibold text-text-dim mb-1';

  return (
    <div>
      <div className="text-2xs text-text-dim uppercase tracking-wider mt-3 mb-1">Payment history</div>
      {history.length === 0 ? (
        <div className="card p-3 text-xs text-text-dim">No payments yet.</div>
      ) : (
        <div className="card p-0 divide-y divide-border">
          {history.map((h) => {
            const isOpen = openId === h.id;
            return (
              <div key={h.id}>
                <div className="flex items-center gap-3 px-3 py-2 hover:bg-surface-2/40 transition-colors">
                  <button data-track="crewAccounting.historyRow" onClick={() => setOpenId(isOpen ? null : h.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <KeyboardArrowRightRoundedIcon sx={{ fontSize: 16 }} className={cn('text-text-dim transition-transform flex-shrink-0', isOpen && 'rotate-90')} />
                    <span className="text-xs text-text-primary w-20 flex-shrink-0">{fmtD(h.date)}</span>
                    <span className="text-2xs px-2 py-0.5 rounded-full bg-surface-2 text-text-dim flex-shrink-0">{h.method}</span>
                    <span className="text-2xs text-text-dim truncate">{h.items.length} item{h.items.length === 1 ? '' : 's'}{h.note ? ` · ${h.note}` : ''}</span>
                  </button>
                  <span className="font-mono text-sm text-green flex-shrink-0">{fmtC(h.amount)}</span>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5 md:gap-0.5 flex-shrink-0">
                      <button onClick={() => setEdit({ id: h.id, amount: String(h.amount ?? ''), method: PAYMENT_METHODS.includes(h.method) ? h.method : PAYMENT_METHODS[0], date: h.date, note: h.note })} disabled={busy} title="Adjust payment" className="p-2 md:p-1 text-text-dim hover:text-accent disabled:opacity-50"><EditRoundedIcon sx={{ fontSize: 16 }} /></button>
                      <button onClick={() => handleDelete(h)} disabled={busy} title="Delete payment" className="p-2 md:p-1 text-text-dim hover:text-red-accent disabled:opacity-50"><DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} /></button>
                    </div>
                  )}
                </div>
                {isOpen && h.items.length > 0 && (
                  <div className="px-9 pb-2 space-y-0.5 bg-surface-2/20">
                    {h.items.map((it) => (
                      <div key={it.id} className="flex items-center justify-between text-2xs py-0.5">
                        <span className="text-text-dim truncate">{it.item || '—'}{it.description ? ` · ${it.description}` : ''}</span>
                        <span className="font-mono text-text-dim ml-2">{fmtC(parseFloat(it.amount) || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Adjust payment modal */}
      {edit && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="card p-5 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-heading text-text-primary">Adjust payment</h3>
              <button onClick={() => setEdit(null)} className="text-text-dim hover:text-text-primary"><CloseRoundedIcon /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div><label className={lblCls}>Amount</label><input type="number" min="0" step="0.01" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className={fieldCls} /></div>
              <div><label className={lblCls}>Method</label><select value={edit.method} onChange={(e) => setEdit({ ...edit, method: e.target.value })} className={fieldCls}>{PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
              <div><label className={lblCls}>Date</label><input type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className={fieldCls} /></div>
              <div><label className={lblCls}>Note <span className="font-normal text-text-dim">(optional)</span></label><input type="text" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className={fieldCls} /></div>
              <button onClick={saveEdit} disabled={busy} className="mt-1 px-4 py-2.5 bg-accent text-bg text-sm font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50">{busy ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
